import React, { Suspense, useEffect } from 'react';
import { Dashboard } from './components/Dashboard';
import { ChunkBoundary } from './components/ChunkBoundary';
import { LoadingSpinner } from './components/LoadingSpinner';
import { CorsService } from './services/corsService';
import { JWTService } from './services/jwtService';
import { isDemoMode } from './services/demoDataService';
import { JWTPayload } from './types';
import './App.css';

// Not reachable by resident users (see the STAFF/production gate below), so it's
// safe to keep out of the main bundle: admin managers ship in their own chunk,
// fetched only when a staff user actually opens the admin view.
const AdminPanel = React.lazy(() => import('./components/admin/AdminPanel'));

function App() {
  const [currentView, setCurrentView] = React.useState<'dashboard' | 'admin'>('dashboard');
  const [jwtPayload, setJwtPayload] = React.useState<JWTPayload | null>(null);
  const [isStaff, setIsStaff] = React.useState<boolean>(false);

  useEffect(() => {
    const initializeApp = async () => {
      // Initialize JWT service with RSA public key
      try {
        await JWTService.initialize();
        
        // Try to get and process JWT token
        const token = JWTService.getToken();
        if (token) {
          const payload = await JWTService.processToken(token);
          if (payload) {
            setJwtPayload(payload);
            // Check for staff role (case-insensitive)
            setIsStaff(payload.role?.toUpperCase() === 'STAFF');
            console.log('🔐 User role:', payload.role);
          }
        } else if (process.env.NODE_ENV === 'development' && !isDemoMode()) {
          // Development mode: Create mock staff JWT payload. Skipped in demo mode —
          // a payload prop would short-circuit Dashboard's own demo branch, which is
          // the whole point of scripts/toggle-demo-mode.js ("no API connection required").
          console.log('🔧 Development mode: Using mock staff credentials');
          const now = Math.floor(Date.now() / 1000);
          const mockPayload: JWTPayload = {
            residentId: 'dev-resident-001',
            facilityId: 'dev-facility-001',
            externalId: 'dev-external-001',
            role: 'STAFF',
            iat: now,
            exp: now + (24 * 60 * 60) // 24 hours from now
          };
          setJwtPayload(mockPayload);
          setIsStaff(true);
        }
      } catch (error) {
        console.error('Failed to initialize JWT service:', error);
        // In development, still provide mock credentials even if JWT fails
        if (process.env.NODE_ENV === 'development' && !isDemoMode()) {
          console.log('🔧 Development mode: Using mock staff credentials (fallback)');
          const now = Math.floor(Date.now() / 1000);
          const mockPayload: JWTPayload = {
            residentId: 'dev-resident-001',
            facilityId: 'dev-facility-001',
            externalId: 'dev-external-001',
            role: 'STAFF',
            iat: now,
            exp: now + (24 * 60 * 60) // 24 hours from now
          };
          setJwtPayload(mockPayload);
          setIsStaff(true);
        }
      }

      // Initialize CORS service
      const cleanup = CorsService.initialize();

      // Check URL for admin mode - only allow if STAFF role or in development
      const urlParams = new URLSearchParams(window.location.search);
      
      if (urlParams.get('mode') === 'admin') {
        // Will be handled by showing admin button only to staff
        setCurrentView('admin');
      }

      return cleanup;
    };

    initializeApp();
  }, []);

  const renderContent = () => {
    switch (currentView) {
      case 'admin':
        // Only allow admin access if STAFF role
        if (!isStaff && process.env.NODE_ENV === 'production') {
          console.warn('⚠️ Admin access denied - STAFF role required');
          return (
            <Dashboard 
              jwtPayload={jwtPayload} 
              isStaff={isStaff}
              onNavigateToAdmin={isStaff ? () => setCurrentView('admin') : undefined}
            />
          );
        }
        return (
          <ChunkBoundary>
            <Suspense fallback={<LoadingSpinner />}>
              <AdminPanel
                jwtPayload={jwtPayload}
                onBack={() => setCurrentView('dashboard')}
              />
            </Suspense>
          </ChunkBoundary>
        );
      case 'dashboard':
      default:
        return (
          <Dashboard 
            jwtPayload={jwtPayload} 
            isStaff={isStaff}
            onNavigateToAdmin={isStaff ? () => setCurrentView('admin') : undefined}
          />
        );
    }
  };

  return (
    <div className="App">
      {renderContent()}
    </div>
  );
}

export default App;
